import { FormField } from '@sunwebgroup/uds-ui/fields/form-field'
import { ListItem } from '@sunwebgroup/uds-ui/list-item'
import { RadioButton, RadioButtonGroup } from '@sunwebgroup/uds-ui/radio-button'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import * as React from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { useIntl } from '@/intl'
import { createGroupsEvent } from '@/scopes/checkout/domain/analytics'
import { FlexContent } from '@/scopes/checkout/groups/components/services/RoomSelection/FlexContent'
import styles from '@/scopes/checkout/groups/components/services/RoomSelection/RoomSelectionService.module.css'
import { isAnyRoomTypeAvailable } from '@/scopes/checkout/groups/components/services/RoomSelection/selectors'
import {
  getErrorMessage,
} from '@/scopes/checkout/groups/components/services/RoomSelection/Sheet/FormFields/getErrorMessages'
import { useAvailableRoomsQuery } from '@/scopes/checkout/groups/domain/hooks'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'

export function RoomTypeFormField() {
  const { formState, setValue, trigger, watch, control } = useFormContext()
  const { data } = useAvailableRoomsQuery()
  const { roomTypes } = data || {}
  const { errors } = formState
  const isAnyRoomTypeAvailableToCreateANamedRoom = isAnyRoomTypeAvailable(roomTypes)
  const intl = useIntl()
  const roomTypeValue = watch('roomType')
  const watchedIsNewRoom = watch('isNewRoom')
  const { sendEvent } = useAnalytics()

  const { field } = useController({
    name: 'roomType',
    control,
    rules: {
      required: watchedIsNewRoom,
    },
  })

  const handleRoomSelectionTypeChange = (value: string) => {
    sendEvent(
      createGroupsEvent({
        description: `RoomSelection`,
        element: 'radio button',
        type: 'selection',
        result: `room type selected`,
      }),
    )

    setValue('roomType', value)
    trigger('roomType')
  }

  const errorMessage = getErrorMessage(
    'roomType',
    errors,
    { requiredErrorMessage: intl.formatMessage({ id: 'groups_checkout_steps_room_selection_room_type_required_error_message' }) },
  )

  if (!isAnyRoomTypeAvailableToCreateANamedRoom)
    return null

  return (
    <FormField
      label={intl.formatMessage({ id: 'groups_checkout_steps_room_selection_room_type_field_label' })}
      feedbackText={errorMessage}
      status={errors.roomType ? 'error' : 'default'}
    >
      <RadioButtonGroup name={field.name} value={roomTypeValue} onValueChange={handleRoomSelectionTypeChange}>
        <FlexContent gap="primitives-space-12">
          {Object.values(roomTypes || []).map(roomType => (
            <ListItem variant="outlined" as="label" key={roomType.type} className={cn(styles.listItemContainerLabel)}>
              <div className={styles.radioButtonWithDescription} style={{ display: 'flex', gap: '8px' }}>
                <RadioButton value={roomType.type} />
                <div className={styles.radioButtonDescription}>
                  <Typography variant="body-m-regular-tight">
                    {intl.formatMessage(
                      { id: 'groups_checkout_steps_room_selection_room_type_field_text' },
                      {
                        amount: roomType.totalBedsAmount,
                        roomType: roomType.description,
                      },
                    )}
                  </Typography>
                  <Typography variant="body-s-regular-tight" className={styles.availableRoomsAmount}>
                    {intl.formatMessage(
                      { id: 'groups_checkout_steps_room_selection_room_type_field_quantity_available' },
                      { amount: roomType.availableRoomsAmount },
                    )}
                  </Typography>
                </div>
              </div>
            </ListItem>
          ))}
        </FlexContent>
      </RadioButtonGroup>
    </FormField>
  )
}
